/**
 * Turns foreign HTML into a schema-conformant fragment.
 *
 * The old editor had no paste handling at all beyond bare URLs, so pasting from
 * Word or Google Docs wrote `mso-` styles, `<font>` tags, tables and images
 * straight into the database -- which is then rendered on public pages through
 * Twig's `|raw`, with no sanitization anywhere in the PHP stack.
 *
 * Parsing happens in a detached `DOMParser` document, which runs no scripts and
 * starts no network requests, and the result is rebuilt by `normalizeFragment`
 * from an allowlist rather than by removing known-bad tags.
 */

import {normalizeFragment} from './normalize';
import {findUrls} from './url';
import {LINK_REL, LINK_TARGET} from './schema';

/** Word marks list paragraphs with these, rather than using real list markup. */
const WORD_LIST_CLASS = /mso-?list/i;

function removeComments(root: Node): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments: Comment[] = [];
  let node = walker.nextNode();
  while (node) {
    comments.push(node as Comment);
    node = walker.nextNode();
  }
  // Also covers Word's `<!--[if gte mso 9]>` blocks and the StartFragment markers.
  for (const comment of comments) {
    comment.remove();
  }
}

/** Reads the bullet Word wrote into the paragraph to decide the list type. */
function wordListType(paragraph: Element): 'UL' | 'OL' {
  const marker = paragraph.textContent?.trimStart() ?? '';
  return /^\d+[.)]/.test(marker) ? 'OL' : 'UL';
}

function isWordListParagraph(element: Element): boolean {
  if (element.nodeName !== 'P') {
    return false;
  }
  const className = element.getAttribute('class') ?? '';
  const style = element.getAttribute('style') ?? '';
  return WORD_LIST_CLASS.test(className) || WORD_LIST_CLASS.test(style);
}

/**
 * Rewrites runs of Word's fake list paragraphs into real lists, dropping the
 * `mso-list: Ignore` span that holds the drawn bullet.
 */
function recoverWordLists(body: HTMLElement): void {
  const paragraphs: Element[] = Array.from(body.querySelectorAll('p')).filter(isWordListParagraph);
  const isInRun = new Set<Element>(paragraphs);
  let index = 0;

  while (index < paragraphs.length) {
    const first = paragraphs[index]!;
    const type = wordListType(first);
    const run: Element[] = [];

    let cursor: Element | null = first;
    while (cursor && isInRun.has(cursor) && wordListType(cursor) === type) {
      run.push(cursor);
      index += 1;
      cursor = cursor.nextElementSibling;
    }

    const list = document.createElement(type.toLowerCase());
    first.before(list);

    for (const paragraph of run) {
      // The first span carries the drawn bullet or number; it is not content.
      const bullet = paragraph.querySelector('span');
      if (bullet && WORD_LIST_CLASS.test(bullet.getAttribute('style') ?? '')) {
        bullet.remove();
      }
      const item = document.createElement('li');
      while (paragraph.firstChild) {
        item.appendChild(paragraph.firstChild);
      }
      list.appendChild(item);
      paragraph.remove();
    }
  }
}

/** Flattens table cells to blocks so their text survives. */
function flattenTables(body: HTMLElement): void {
  for (const cell of Array.from(body.querySelectorAll('td, th'))) {
    const paragraph = document.createElement('p');
    while (cell.firstChild) {
      paragraph.appendChild(cell.firstChild);
    }
    cell.replaceWith(paragraph);
  }
}

/**
 * Collapses the runs of whitespace and non-breaking spaces that Word emits.
 * Only ever applied to pasted content: the steady-state normalizer must not
 * touch whitespace, because selection bookmarks are text offsets.
 */
function collapseWhitespace(root: Node): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    const collapsed = text.data
      .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
      .replace(/[\u00A0\u2007\u202F]/g, ' ')
      .replace(/[\u2028\u2029]/g, '\n')
      .replace(/\s+/g, ' ');
    if (collapsed !== text.data) {
      text.data = collapsed;
    }
    node = walker.nextNode();
  }
}

/** Wraps bare URLs found in text nodes, skipping anything already linked. */
export function autolinkFragment(fragment: DocumentFragment | HTMLElement): void {
  const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    texts.push(node as Text);
    node = walker.nextNode();
  }

  for (const text of texts) {
    if (text.parentElement?.closest('a')) {
      continue;
    }
    const matches = findUrls(text.data);
    // Apply from the back so earlier offsets stay valid.
    for (const match of [...matches].reverse()) {
      const tail = text.splitText(match.start);
      tail.splitText(match.end - match.start);
      const anchor = document.createElement('a');
      anchor.setAttribute('href', match.href);
      anchor.setAttribute('target', LINK_TARGET);
      anchor.setAttribute('rel', LINK_REL);
      tail.before(anchor);
      anchor.appendChild(tail);
    }
  }
}

/** Clipboard HTML -> schema-conformant fragment. */
export function parseExternalHtml(html: string): DocumentFragment {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const body = parsed.body;

  removeComments(body);
  recoverWordLists(body);
  flattenTables(body);
  collapseWhitespace(body);

  const fragment = normalizeFragment(body);
  autolinkFragment(fragment);
  return fragment;
}

/**
 * Plain text -> fragment. Blank lines separate paragraphs; a single newline
 * becomes a line break, so pasted plain text keeps its shape.
 */
export function plainTextToFragment(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const paragraphs = text.replace(/\r\n?/g, '\n').split(/\n{2,}/);

  for (const chunk of paragraphs) {
    const paragraph = document.createElement('p');
    const lines = chunk.split('\n');
    lines.forEach((line, position) => {
      if (position > 0) {
        paragraph.appendChild(document.createElement('br'));
      }
      paragraph.appendChild(document.createTextNode(line.replace(/\t/g, ' ')));
    });
    if (paragraph.childNodes.length === 0) {
      paragraph.appendChild(document.createElement('br'));
    }
    fragment.appendChild(paragraph);
  }

  autolinkFragment(fragment);
  return fragment;
}

/** Stored value -> fragment, used when a value is written into the editor. */
export function parseValue(html: string): DocumentFragment {
  console.log('parseValue', html);

  /*
     IMPORTANT: html string is compile time check
     add check if html is a string, otherwise no trim can be called
  */
  let trimmedValue: string = '';
  if (typeof html === 'string') {
    trimmedValue = html.trim();
  }else{
    trimmedValue = html;
  }

  if (!trimmedValue) {
    console.log('parseValue: empty html');
    const fragment = document.createDocumentFragment();
    const paragraph = document.createElement('p');
    paragraph.appendChild(document.createElement('br'));
    fragment.appendChild(paragraph);
    return fragment;
  }

  const parsed = new DOMParser().parseFromString(trimmedValue, 'text/html');
  removeComments(parsed.body);
  return normalizeFragment(parsed.body);
}
